import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, Loader2, ShieldCheck } from "lucide-react";

export const EmailSettingsDialog = ({ open, onOpenChange }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ sender_name: "", mailing_address: "" });
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({
    queryKey: ["email-settings"],
    queryFn: () => api.get("/settings/email").then((r) => r.data),
    enabled: open,
  });
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/settings/email", form);
      qc.setQueryData(["email-settings"], r.data);
      toast.success("Email settings saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-w-lg" data-testid="email-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Email settings</DialogTitle>
          <DialogDescription>Shown in the footer of every outreach email, as Canada's anti-spam law (CASL) requires.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Sender name</Label>
            <Input className="mt-1" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="Hygio" data-testid="email-settings-sender" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Mailing address</Label>
            <Textarea className="mt-1" rows={2} value={form.mailing_address}
              onChange={(e) => setForm({ ...form, mailing_address: e.target.value })}
              placeholder="123 Ouellette Ave, Windsor, ON N9A 1A1" data-testid="email-settings-address" />
            {!form.mailing_address && (
              <p className="text-xs text-amber-400 mt-1.5" data-testid="email-settings-address-warning">
                Required before campaigns can email leads whose address was found on their website.
              </p>
            )}
          </div>
          <div className="rounded-lg border border-border bg-background/40 p-3 text-xs text-muted-foreground" data-testid="email-settings-preview">
            <p className="flex items-center gap-1.5 text-foreground/80 font-medium mb-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Footer preview</p>
            Sent by {form.sender_name || "Hygio"}<br />
            {form.mailing_address && <>{form.mailing_address}<br /></>}
            We're reaching out because your business email is published on your website… <u>Unsubscribe</u>
          </div>
          <Button onClick={save} disabled={saving || !form.sender_name.trim()} className="w-full" data-testid="email-settings-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
